import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  ChevronRight,
  Clock,
  Download,
  FileSpreadsheet,
  Inbox,
  KeyRound,
  Plus,
  Search,
  Wallet,
} from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type {
  EmpresaCaptura,
  Job,
  Resumo,
  ResumoCreditos,
} from '../../api/tipos';
import { useSessao } from '../../auth/SessaoContext';
import {
  Aviso,
  BotaoLink,
  Cartao,
  Celula,
  Esqueleto,
  Etiqueta,
  Indicador,
  Indicadores,
  Tabela,
  Td,
  Th,
  Vazio,
} from '../../componentes/ui';
import {
  DuasColunas,
  Pagina,
  Pilha,
  TituloPagina,
} from '../../componentes/Pagina';
import {
  estimativaDownload,
  numero,
  plural,
  reais,
  tempoRelativo,
} from '../../util/formato';
import { etiquetaDoJob } from './statusJob';
import { etiquetaEmpresa } from './captura/status';
import css from './VisaoGeral.module.css';
import { useCapturaLiberada } from './captura/liberacao';

/** Sobe para 5 s quando há job rodando: é quando o número muda de verdade. */
const INTERVALO_ATIVO_MS = 5_000;
const INTERVALO_PARADO_MS = 30_000;
/** Quantas buscas aparecem no quadro de recentes; o resto fica em Downloads. */
const RECENTES = 5;

export function VisaoGeral() {
  const { usuario } = useSessao();
  const capturaLiberada = useCapturaLiberada();
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [creditos, setCreditos] = useState<ResumoCreditos | null>(null);
  /** `null` enquanto carrega; lista vazia também quando a consulta falha. */
  const [empresas, setEmpresas] = useState<EmpresaCaptura[] | null>(null);

  useEffect(() => {
    let vivo = true;
    let timer: number | undefined;

    async function ciclo() {
      try {
        const [r, j] = await Promise.all([
          api<Resumo>('/nfe/lote/resumo'),
          api<Job[]>('/nfe/lote'),
        ]);
        if (!vivo) return;
        setResumo(r);
        setJobs(j);
        setErro(null);
        // Recursivo em vez de setInterval: sem sobreposição se a resposta
        // demorar mais que o intervalo.
        timer = window.setTimeout(
          () => void ciclo(),
          r.jobsEmAndamento > 0 ? INTERVALO_ATIVO_MS : INTERVALO_PARADO_MS,
        );
      } catch (err) {
        if (!vivo) return;
        setErro(
          err instanceof ErroApi
            ? err.message
            : 'Não foi possível carregar seus números.',
        );
        timer = window.setTimeout(() => void ciclo(), INTERVALO_PARADO_MS);
      }
    }

    void ciclo();
    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Saldo e captura mudam pouco: uma consulta ao abrir basta. O saldo também
  // é reconsultado no evento `bx:saldo`, o mesmo que atualiza a lateral depois
  // de um Pix confirmado.
  useEffect(() => {
    let vivo = true;
    const buscarSaldo = () =>
      api<ResumoCreditos>('/creditos')
        .then((c) => vivo && setCreditos(c))
        .catch(() => undefined);

    buscarSaldo();
    // Falha aqui não é motivo para tirar o painel do ar: sem a lista, o quadro
    // da captura simplesmente não aparece.
    api<EmpresaCaptura[]>('/dfe/empresas')
      .then((e) => vivo && setEmpresas(e))
      .catch(() => vivo && setEmpresas([]));

    window.addEventListener('bx:saldo', buscarSaldo);
    return () => {
      vivo = false;
      window.removeEventListener('bx:saldo', buscarSaldo);
    };
  }, []);

  const primeiroNome = usuario?.nome.split(' ')[0] ?? '';
  const recentes = (jobs ?? []).slice(0, RECENTES);
  const temCaptura = (empresas?.length ?? 0) > 0;
  const notasCapturadas = (empresas ?? []).reduce(
    (s, e) => s + e.notas.total,
    0,
  );

  return (
    <Pagina>
      <TituloPagina
        titulo={primeiroNome ? `Olá, ${primeiroNome}` : 'Visão geral'}
        descricao={
          resumo === null
            ? 'Carregando seus números…'
            : resumo.totalJobs === 0
              ? 'Você ainda não fez nenhuma busca. Comece colando suas chaves.'
              : `${plural(resumo.totalJobs, 'busca')} na sua conta${
                  resumo.jobsEmAndamento > 0
                    ? `, ${numero(resumo.jobsEmAndamento)} rodando agora`
                    : ''
                }.`
        }
      />

      {erro && <Aviso>{erro}</Aviso>}

      <Indicadores>
        <Indicador
          rotulo="Saldo disponível"
          icone={<Wallet />}
          para="/painel/creditos"
          valor={
            creditos ? (
              reais(creditos.saldoCentavos)
            ) : (
              <Esqueleto altura={28} largura={110} />
            )
          }
          detalhe={
            creditos && creditos.reservadoCentavos > 0
              ? `${reais(creditos.reservadoCentavos)} reservado em buscas`
              : 'Ver extrato e recarregar'
          }
        />
        <Indicador
          rotulo="XMLs baixados"
          icone={<Download />}
          para="/painel/downloads"
          valor={
            resumo ? (
              numero(resumo.xmlsBaixados)
            ) : (
              <Esqueleto altura={28} largura={90} />
            )
          }
          detalhe={
            resumo &&
            (resumo.naoEncontradas > 0 || resumo.comErro > 0 ? (
              <>
                {plural(
                  resumo.naoEncontradas,
                  'não encontrada',
                  'não encontradas',
                )}
                {resumo.comErro > 0 && (
                  <>
                    {' · '}
                    <span className={css.falhas}>
                      {numero(resumo.comErro)} com erro
                    </span>
                  </>
                )}
              </>
            ) : (
              'Somando todas as buscas'
            ))
          }
        />
        <Indicador
          rotulo="Chaves na fila"
          icone={<Clock />}
          tom={resumo && resumo.pendentes > 0 ? 'alerta' : undefined}
          valor={
            resumo ? (
              numero(resumo.pendentes)
            ) : (
              <Esqueleto altura={28} largura={60} />
            )
          }
          detalhe={
            resumo &&
            (resumo.pendentes > 0
              ? `${plural(resumo.jobsEmAndamento, 'busca')} em andamento · ${estimativaDownload(resumo.pendentes)}`
              : 'Nenhuma busca rodando')
          }
        />
        {temCaptura && (
          <Indicador
            rotulo="Notas capturadas"
            icone={<Inbox />}
            para="/painel/notas-recebidas"
            valor={numero(notasCapturadas)}
            detalhe={`De ${plural(empresas!.length, 'empresa')} na captura automática`}
          />
        )}
      </Indicadores>

      <nav className={css.atalhos} aria-label="Atalhos">
        <Atalho
          para="/painel/nova-busca"
          icone={<KeyRound />}
          titulo="Nova busca"
          descricao="Cole chaves de acesso ou envie um CSV."
        />
        <Atalho
          para="/painel/sped"
          icone={<FileSpreadsheet />}
          titulo="Enviar SPED"
          descricao="Extraia as chaves direto da EFD e escolha quais baixar."
        />
        {capturaLiberada && (
          <Atalho
            para="/painel/empresas"
            icone={<Building2 />}
            titulo="Captura automática"
            descricao="Receba as notas de entrada dos seus clientes sem precisar das chaves."
          />
        )}
      </nav>

      <DuasColunas
        principal={
          <Cartao
            padding={0}
            titulo="Buscas recentes"
            acoes={
              jobs && jobs.length > 0 ? (
                <BotaoLink
                  to="/painel/downloads"
                  aparencia="discreto"
                  pequeno
                  iconeDireita={<ChevronRight />}
                >
                  Ver todas
                </BotaoLink>
              ) : undefined
            }
          >
            {jobs === null ? (
              <div className={css.carregando}>
                <Esqueleto altura={20} />
                <Esqueleto altura={20} />
                <Esqueleto altura={20} />
              </div>
            ) : recentes.length === 0 ? (
              <Vazio
                icone={<Search />}
                titulo="Nenhuma busca ainda"
                descricao="Cole as chaves de acesso ou envie um CSV para começar a baixar os XMLs."
                acao={
                  <BotaoLink to="/painel/nova-busca" icone={<Plus />}>
                    Começar uma busca
                  </BotaoLink>
                }
              />
            ) : (
              <Tabela rotulo="Buscas recentes">
                <thead>
                  <tr>
                    <Th>Busca</Th>
                    <Th>Situação</Th>
                    <Th alinhar="direita">Baixados</Th>
                  </tr>
                </thead>
                <tbody>
                  {recentes.map((job) => {
                    const etiqueta = etiquetaDoJob(job);
                    return (
                      <tr key={job.id}>
                        <Td className={css.semQuebra}>
                          <Celula
                            para={`/painel/downloads/${job.id}`}
                            principal={plural(job.total, 'chave')}
                            secundaria={tempoRelativo(job.createdAt)}
                          />
                        </Td>
                        <Td>
                          <Etiqueta
                            tom={etiqueta.tom}
                            pulsando={etiqueta.pulsando}
                          >
                            {etiqueta.texto}
                          </Etiqueta>
                        </Td>
                        <Td
                          alinhar="direita"
                          className={`num ${css.semQuebra}`}
                        >
                          {numero(job.ok)}
                          <span className={css.deTotal}>
                            {' '}
                            de {numero(job.total)}
                          </span>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Tabela>
            )}
          </Cartao>
        }
        lateral={
          <Pilha>
            {temCaptura && <ResumoCaptura empresas={empresas!} />}
            <TempoDeDownload pendentes={resumo?.pendentes} />
          </Pilha>
        }
      />
    </Pagina>
  );
}

function Atalho({
  para,
  icone,
  titulo,
  descricao,
}: {
  para: string;
  icone: ReactNode;
  titulo: string;
  descricao: string;
}) {
  return (
    <Link to={para} className={css.atalho}>
      <span className={css.atalhoIcone} aria-hidden="true">
        {icone}
      </span>
      <span className={css.atalhoTexto}>
        <span className={css.atalhoTitulo}>{titulo}</span>
        <span className={css.atalhoDescricao}>{descricao}</span>
      </span>
      <ChevronRight className={css.atalhoSeta} aria-hidden="true" />
    </Link>
  );
}

/**
 * Captura automática em uma olhada: situação de cada CNPJ, quantos XMLs já
 * chegaram e o que está esperando o cliente (manifestação no ERP).
 */
function ResumoCaptura({ empresas }: { empresas: EmpresaCaptura[] }) {
  const soma = (f: (e: EmpresaCaptura) => number) =>
    empresas.reduce((s, e) => s + f(e), 0);
  const completas = soma((e) => e.notas.completas);
  const aguardando = soma((e) => e.notas.aguardandoXml);
  const foraDoPrazo = soma((e) => e.notas.foraDoPrazo);
  const visiveis = empresas.slice(0, 3);

  return (
    <Cartao
      titulo="Captura automática"
      acoes={
        <BotaoLink
          to="/painel/notas-recebidas"
          aparencia="discreto"
          pequeno
          iconeDireita={<ChevronRight />}
        >
          Ver notas
        </BotaoLink>
      }
    >
      <div className={css.captura}>
        <ul className={css.empresas}>
          {visiveis.map((e) => {
            const etiqueta = etiquetaEmpresa(e);
            return (
              <li key={e.id} className={css.empresa}>
                <span className={css.empresaNome} title={e.nome}>
                  {e.nome}
                </span>
                <Etiqueta tom={etiqueta.tom}>{etiqueta.texto}</Etiqueta>
              </li>
            );
          })}
          {empresas.length > visiveis.length && (
            <li className={css.empresaMais}>
              <Link to="/painel/empresas">
                e mais {plural(empresas.length - visiveis.length, 'empresa')}
              </Link>
            </li>
          )}
        </ul>

        <div className={css.totalXml}>
          <span>XMLs completos recebidos</span>
          <span className="num">
            {numero(completas)}
            {aguardando > 0 && ` · ${numero(aguardando)} a caminho`}
          </span>
        </div>

        {foraDoPrazo > 0 && (
          <ul className={css.pendencias}>
            <li>
              <span className={css.pendenciaAlerta}>
                {plural(foraDoPrazo, 'nota', 'notas')}
              </span>{' '}
              para manifestar no seu ERP.
            </li>
          </ul>
        )}
      </div>
    </Cartao>
  );
}

/**
 * Expectativa de tempo.
 *
 * A pergunta que aparece assim que alguém manda a primeira busca grande é
 * "travou?". Não travou: a fonte throttla, e um lote de dezenas de milhares de
 * notas leva dias. Dizer isso ANTES de a pessoa se assustar é o que evita o
 * cancelamento no meio — que, além de perder o trabalho, cobra o que já foi
 * entregue.
 *
 * Com fila em andamento, mostra a estimativa do que falta em vez da frase
 * genérica: um número específico responde melhor do que "pode demorar".
 */
function TempoDeDownload({ pendentes }: { pendentes: number | undefined }) {
  const temFila = (pendentes ?? 0) > 0;

  return (
    <Cartao titulo="Quanto tempo leva">
      <div className={css.tempo}>
        {temFila && (
          <Aviso
            tom="alerta"
            titulo={`${plural(pendentes ?? 0, 'chave')} na fila · ${estimativaDownload(pendentes ?? 0)}`}
          >
            Estimativa pelo ritmo da fonte. Pode fechar esta aba — a busca
            continua rodando.
          </Aviso>
        )}

        <ul className={css.lista}>
          <li>
            O tempo depende da <strong>quantidade de notas</strong>. Uns
            milhares saem em horas; dezenas de milhares levam{' '}
            <strong>dias</strong>. Não é lentidão nossa: a fonte da Receita
            limita quantas consultas aceita por vez, e acelerar além disso só
            faz ela recusar.
          </li>
          <li>
            A busca roda em <strong>segundo plano</strong>. Pode fechar a aba,
            desligar o computador e voltar depois — e se o serviço reiniciar,
            ela continua de onde parou.
          </li>
          <li>
            Não precisa esperar terminar: dá para{' '}
            <strong>baixar o ZIP parcial</strong> com o que já chegou, a
            qualquer momento.
          </li>
        </ul>
      </div>
    </Cartao>
  );
}
